import tracking from 'dibs-tracking';
import serverVars from 'server-vars';
import { Context } from '@datadog/browser-core';
import { BROWSE_TYPES, PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { getFCP, getLCP, getFID, getCLS, getTTFB, Metric } from 'web-vitals';

/**
 * `responseStart` is a metric provided by the Resource Timing Level 2 API.
 *
 * Spec - https://www.w3.org/TR/resource-timing-2/#dom-performanceresourcetiming
 * Order of `Navigation Timing` metrics - https://w3c.github.io/navigation-timing/#processing-model
 */
const PERF_ENTRY_DEFAULT_START_MARK = 'responseStart';

const PERF_METRIC_BROWSER_FCP = 'FCP';
const PERF_METRIC_BROWSER_LCP = 'LCP';
const PERF_METRIC_BROWSER_FID = 'FID';
const PERF_METRIC_BROWSER_CLS = 'CLS';
const PERF_METRIC_BROWSER_TTFB = 'TTFB';
const PERF_METRIC_CUSTOM_HYDRATE = 'react-hydrate';
const PERF_METRIC_CUSTOM_AUTH_MODAL_LOAD = 'custom-auth-modal-load';

/**
 * Configuration objects to determine what will be uploaded to Google Analytics
 * and Datadog.
 */
const UPLOAD_TO_DD = [PERF_METRIC_BROWSER_FID, PERF_METRIC_CUSTOM_AUTH_MODAL_LOAD];
const UPLOAD_TO_GA = [
    PERF_METRIC_CUSTOM_HYDRATE,
    PERF_METRIC_BROWSER_FCP,
    PERF_METRIC_BROWSER_LCP,
    PERF_METRIC_BROWSER_FID,
    PERF_METRIC_BROWSER_CLS,
    PERF_METRIC_BROWSER_TTFB,
];
/**
 * GA limits how many samples they will process daily, therefore, we're only
 * firing performance metrics for these page types to more quickly gather data
 * https://developers.google.com/analytics/devguides/collection/analyticsjs/user-timings#sampling_considerations
 */
const UPLOAD_TO_GA_PAGETYPES = [PAGE_TYPE.PDP, ...BROWSE_TYPES, PAGE_TYPE.SEARCH];

const metricsReportedToDataDog: string[] = [];

function shouldUploadToDataDog(name: string): boolean {
    return UPLOAD_TO_DD.includes(name) && !metricsReportedToDataDog.includes(name);
}

function shouldUploadToAnalytics(name: string): boolean {
    const pageType = serverVars.get('settings.pageType');
    return (
        !serverVars.get('enablers.disableGaPerformanceTracking') &&
        UPLOAD_TO_GA.includes(name) &&
        UPLOAD_TO_GA_PAGETYPES.includes(pageType)
    );
}

/**
 * Use these constants when calling mark or measure.
 */
export const PERF_METRICS = {
    HYDRATE: PERF_METRIC_CUSTOM_HYDRATE,
    AUTH_MODAL_LOAD: PERF_METRIC_CUSTOM_AUTH_MODAL_LOAD,
};

const supportsPerformanceObserver =
    typeof window === 'object' &&
    window.PerformanceObserver &&
    !!window.PerformanceObserver.supportedEntryTypes;

// Know when the entry types we would like to use are not supported.
function isSupportedPerfEntryType(entryTypes: string[]): boolean {
    for (const entryType of entryTypes) {
        if (!window.PerformanceObserver.supportedEntryTypes.includes(entryType)) {
            return false;
        }
    }
    return true;
}

function msToNs(duration: number): number {
    // datadog stores duration in ns and PerformanceEntry gives use ms.
    // to stay consistent we will convert duration to ns.
    return +(duration * 1e6).toFixed(0);
}

function roundToMs(milliseconds: number): number {
    return Math.round(milliseconds);
}

function addDatadogUserAction(name: string, context: Context): void {
    if (window.DD_RUM) {
        window.DD_RUM.addUserAction(name, context);
        metricsReportedToDataDog.push(name);
    }
}

function trackUserTiming(timingCategory: string, timingVar: string, timingValue: number): void {
    const pageType = serverVars.get('settings.pageType');
    let timingLabel = '';
    // mimicking convention used for GA pageview page types for consistency
    if (BROWSE_TYPES.includes(pageType) || pageType === PAGE_TYPE.SEARCH) {
        timingLabel = 'Results';
    } else if (pageType === PAGE_TYPE.PDP) {
        timingLabel = 'Products';
    }

    if (timingLabel) {
        tracking.trackUserTiming({
            category: timingCategory,
            var: timingVar,
            value: roundToMs(timingValue),
            label: timingLabel,
        });
    }
}

function trackWebVitals({ delta, name, id }: Pick<Metric, 'delta' | 'name' | 'id'>): void {
    if (shouldUploadToDataDog(name)) {
        addDatadogUserAction('First Input Delay', {
            duration: msToNs(delta),
        });
    }
    if (shouldUploadToAnalytics(name)) {
        tracking.trackWebVitalsEvent({
            action: name,
            value: Math.round(name === 'CLS' ? delta * 1000 : delta),
            label: id,
        });
    }
}

export function markPerfMetric(name: string): void {
    if (supportsPerformanceObserver) {
        window.performance.mark(name);
    }
}

export function measurePerfMetric(
    name: string,
    /**
     * In most cases we will want to measure things from the default mark, so not to include server
     * response times.
     */
    startMark = PERF_ENTRY_DEFAULT_START_MARK,
    endMark?: string
): void {
    if (supportsPerformanceObserver) {
        window.performance.measure(name, startMark, endMark);
    }
}

export function initCustomPerformanceReporting(): void {
    if (!supportsPerformanceObserver) {
        return;
    }

    if (isSupportedPerfEntryType(['measure'])) {
        const customObserver = new PerformanceObserver(list => {
            for (const entry of list.getEntries()) {
                const name = entry.name;
                if (shouldUploadToDataDog(name)) {
                    addDatadogUserAction(name, {
                        duration: msToNs(entry.duration),
                    });
                }

                if (shouldUploadToAnalytics(name)) {
                    if (entry.duration !== 0) {
                        trackUserTiming(entry.name, 'duration', entry.duration);
                    }
                    if (entry.startTime !== 0) {
                        trackUserTiming(entry.name, 'startTime', entry.startTime);
                    }
                    // A `startTime` of 0 is valid for when we're calculating `endTime`
                    if (entry.duration !== 0 && entry.startTime >= 0) {
                        trackUserTiming(entry.name, 'endTime', entry.startTime + entry.duration);
                    }
                }
            }
        });

        customObserver.observe({
            entryTypes: ['measure'],
        });
    }

    getFID(trackWebVitals);
    getFCP(trackWebVitals);
    getLCP(trackWebVitals);
    getCLS(trackWebVitals);
    getTTFB(trackWebVitals);
}
